// src/store/loadingMiddleware.ts
import type { Middleware, UnknownAction } from "@reduxjs/toolkit";
import { showGlobalLoading, hideGlobalLoading } from "./globalLoadingSlice";

/**
 * loadingMiddleware (meta-first)
 * - Prefer reading endpointName from action.meta (RTK Query provides this).
 * - Fallback order:
 *    1. action.meta.endpointName
 *    2. action.meta.arg.endpointName
 *    3. action.meta.arg.originalArgs.endpointName
 *    4. action.meta.reducerPath (api name)
 *
 * - Map deployApi endpoints to friendly labels below.
 */

const deployApiEndpointLabels: Record<string, string> = {
  deployApp: "Deploy Agent runnig",
  fetchAppPreview: "Fetching Preview",
  fixAppErrors:  "Debug Agent running",
  modifyAppCode: "Coding Agent running",
  manualFileUpdate: "Manual Update running",
  generateCode: "Coding Agent running",
  regenerateCode: "Coding Agent running",
  generateDesign: "Design Agent running",
  generateImages: "Image Generation Running",
  fetchImages: "Image Generation Running",
  fetchProjectInfo: "Fetching Project Info",
  buildAndPublish: "Publishing App",
  unpublishApp: "Unpublishing App",
};

function getEndpointFromMeta(action: UnknownAction) {
  const meta: any = (action as any).meta;
  if (!meta || typeof meta !== "object") return { apiName: null, endpointName: null, lifecycle: null };

  // lifecycle: RTK Query usually sets action.type like ".../pending" etc,
  // but we prefer to deduce lifecycle from the type string safely:
  const typeStr = typeof (action as any).type === "string" ? (action as any).type : "";
  const lifecycleMatch = typeStr.match(/\/(pending|fulfilled|rejected)$/);
  const lifecycle = lifecycleMatch ? lifecycleMatch[1] : null;

  // apiName (reducerPath) if available
  const apiName = typeof meta.reducerPath === "string" ? meta.reducerPath : null;

  // 1) direct meta.endpointName
  if (typeof meta.endpointName === "string") {
    return { apiName, endpointName: meta.endpointName, lifecycle };
  }

  // 2) meta.arg might be a string or object depending on RTK Query version.
  const arg = meta.arg;
  if (arg) {
    // If arg is an object with endpointName
    if (typeof arg === "object") {
      if (typeof arg.endpointName === "string") {
        return { apiName, endpointName: arg.endpointName, lifecycle };
      }

      // Some shapes put originalArgs.endpointName
      if (arg.originalArgs && typeof arg.originalArgs === "object") {
        if (typeof arg.originalArgs.endpointName === "string") {
          return { apiName, endpointName: arg.originalArgs.endpointName, lifecycle };
        }

        // If originalArgs is something like { app_project_id: "...", ... }
        // we can't reliably get endpointName here; so continue
      }
    }

    // Sometimes arg is a string or primitive — ignore
  }

  // Fallback: if no endpoint name found, return apiName only.
  return { apiName, endpointName: null, lifecycle };
}

export const loadingMiddleware: Middleware =
  (storeAPI) => (next) => (action: any) => {
    // Extract meta-first
    const { apiName, endpointName, lifecycle } = getEndpointFromMeta(action);


    // If lifecycle can't be derived, do a best-effort parse from action.type
    // (rare, but safe)
    let finalLifecycle = lifecycle;
    if (!finalLifecycle) {
      const typeStr = typeof (action as any).type === "string" ? (action as any).type : "";
      const m = typeStr.match(/\/(pending|fulfilled|rejected)$/);
      finalLifecycle = m ? m[1] : null;
    }

    if (!finalLifecycle) {
      return next(action);
    }

    // Optional: ignore some APIs (e.g. autoDebug) from global loader
    if (apiName === "autoDebug") return next(action);

    // Decide label: prefer endpointName, otherwise apiName
    let label = endpointName ?? apiName ?? "Working";

    // For deployApi, map endpointName to friendly text if available
     if (endpointName) {
        label = deployApiEndpointLabels[endpointName] ?? endpointName;
    }
    

    if (finalLifecycle === "pending") {
      storeAPI.dispatch(showGlobalLoading({ endpoint: label }));
    } else {
      // fulfilled or rejected
      storeAPI.dispatch(hideGlobalLoading());
    }

    return next(action);
  };
